import React, { useState, useEffect, useRef, useCallback } from "react";
import { Progress, Badge, notification, message } from "antd";
import {
  getActionsFromStore,
  deleteActionFromStore,
  offlineHandler,
  saveActionToStore,
  getUnsyncedLocationRecords,
  markLocationRecordsSynced,
} from "../../../utils/offlineCache";
import { SyncOutlined, HourglassOutlined, CheckCircleTwoTone, LoadingOutlined } from "@ant-design/icons";
import { useAuth } from "services/Auth";
import SyncBlockingModal from "components/SyncBlockingModal";
import { LOCATION_SYNC_BATCH_SIZE } from "constants/location";
import { isOnline as isNetworkOnline } from "utils/networkConnection";

const Sync = () => {
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);

  const [isLoading, setIsLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);
  const [syncStatusMessage, setSyncStatusMessage] = useState("A sincronizar ações pendentes...");
  const checkInFlightRef = useRef(false);
  const syncActionsRef = useRef(null);
  const locationSyncInFlightRef = useRef(false);
  const syncLocationRecordsRef = useRef(null);
  const authUserName = auth?.data?.name || "Unknown User";

  useEffect(() => {
    const handleOnline = () => {
      // Send info capture to the server for debugging purposes
      apiClient.post("/info-capture", {
        info: null,
        extraData: { location: "/components/layout-components/SyncPanel/index.jsx/useEffect", timestamp: new Date().toISOString() },
        subject: "Boats Mobile SyncPanel",
        message: `SyncPanel: Internet Online (${authUserName})`,
      });
      setIsOnline(true);
    };
    const handleOffline = () => {
      // Send info capture to the server for debugging purposes
      apiClient.post("/info-capture", {
        info: null,
        extraData: { location: "/components/layout-components/SyncPanel/index.jsx/useEffect", timestamp: new Date().toISOString() },
        subject: "Boats Mobile SyncPanel",
        message: `SyncPanel: Internet Offline (${authUserName})`,
      });
      setIsOnline(false);
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      setIsLoading(false);
      setIsSyncing(false);
      setSyncProgress(0);
      setPendingActions([]);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const checkPendingActions = useCallback(async () => {
    if (!isOnline) return;
    if (checkInFlightRef.current) return;
    checkInFlightRef.current = true;

    try {
      if (isLoading || isSyncing) return;
      setIsLoading(true);
      const pendingActionsResponse = await getActionsFromStore();
      if (pendingActionsResponse.status === false) {
        notification.error({
          message: "Crap!",
          description: "Não foi possível aceder às ações pendentes para sincronização! Call ghostbusters!",
        });

        // Capture the error for debugging purposes
        await apiClient.post("/error-capture", {
          error: pendingActionsResponse.data,
          action: "/components/layout-components/SyncPanel/index.jsx/checkPendingActions/pendingActionsResponse-status-false",
          timestamp: new Date().toISOString(),
        });

        setIsLoading(false);
        return;
      }

      const pendingActions = pendingActionsResponse.data || [];

      if (pendingActions.length === 0) {
        setPendingActions([]);
        setIsLoading(false);
        return;
      }

      setPendingActions(pendingActions);
      setIsLoading(false);
      if (isOnline && pendingActions.length > 0 && typeof syncActionsRef.current === "function") {
        syncActionsRef.current(pendingActions);
      }
    } finally {
      checkInFlightRef.current = false;
    }
  }, [isOnline, isLoading, isSyncing]);

  useEffect(() => {
    if (!isOnline) return undefined;
    const intervalId = setInterval(() => {
      checkPendingActions();
      // Same engine, second branch: drain unsynced GPS location records too.
      if (typeof syncLocationRecordsRef.current === "function") {
        syncLocationRecordsRef.current();
      }
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isOnline, checkPendingActions]);

  async function syncActions(actions) {
    if (isSyncing) return;
    setIsSyncing(true);
    setSyncStatusMessage("A sincronizar ações pendentes...");
    try {
      let processedCount = 0;
      let syncedCount = 0;
      let failedCount = 0;
      let invalidAuthWarningShown = false;
      const totalActions = actions.length;
      const remainingActionIds = new Set();

      // Send info capture to the server for debugging purposes
      await apiClient.post("/info-capture", {
        extraData: { location: "/components/layout-components/SyncPanel/index.jsx/syncActions", timestamp: new Date().toISOString() },
        subject: "Boats: Mobile SyncPanel",
        message: `Boats: SyncPanel: Starting synchronization of pending actions (${totalActions} actions) (${authUserName})`,
      });

      for (const action of actions) {
        const id = action.id;
        setSyncStatusMessage(`A sincronizar ação ${processedCount + 1} de ${totalActions}...`);
        const actionAuth = action?.queued_by || null;
        const actionMethod = action?.method ? String(action.method).toLowerCase() : "";
        const currentAuth = auth?.data || null;
        const currentAuthUserId = currentAuth?._id || currentAuth?.user || currentAuth?.id || null;
        const actionAuthUserId = actionAuth?.user_id || actionAuth?._id || actionAuth?.id || null;
        const canHydrateActionToken =
          actionAuthUserId && !actionAuth?.token && currentAuthUserId && currentAuth?.token && String(actionAuthUserId) === String(currentAuthUserId);
        const hasValidActionAuth = actionAuthUserId && (actionAuth?.token || canHydrateActionToken);

        if (!actionMethod || typeof apiClient[actionMethod] !== "function") {
          notification.error({
            message: "Erro de Sincronização",
            description: `A ação ${id} tem um método inválido e foi mantida na fila.`,
          });
          failedCount++;
          remainingActionIds.add(id);
          processedCount++;
          setSyncProgress(Math.round((processedCount / totalActions) * 100));
          continue;
        }

        if (!hasValidActionAuth) {
          if (!invalidAuthWarningShown) {
            notification.warning({
              message: "Ações offline inválidas na fila",
              description:
                "Foram encontradas ações antigas sem identificação/token válido do staff. Mantivemos essas ações na fila para evitar perda de dados. Faz login online com o staff correto para recriar/repetir a operação.",
            });
            invalidAuthWarningShown = true;
          }

          failedCount++;
          remainingActionIds.add(id);
          processedCount++;
          setSyncProgress(Math.round((processedCount / totalActions) * 100));
          continue;
        }

        if (canHydrateActionToken) {
          await saveActionToStore({
            ...action,
            queued_by: {
              ...actionAuth,
              token: currentAuth.token,
            },
          });
        }

        const resolvedActionAuth =
          canHydrateActionToken && actionAuthUserId
            ? {
                ...actionAuth,
                user_id: actionAuthUserId,
                token: currentAuth.token,
              }
            : actionAuth;

        const actionClient =
          resolvedActionAuth?.user_id && resolvedActionAuth?.token
            ? offlineHandler({
                _id: resolvedActionAuth.user_id,
                token: resolvedActionAuth.token,
              })
            : apiClient;

        const response = await actionClient[actionMethod](action.url, action.data);
        processedCount++;
        setSyncProgress(Math.round((processedCount / totalActions) * 100));

        if (response.status === false) {
          notification.error({
            message: "Erro de Sincronização",
            description: `Não foi possível sincronizar a ação ${id}. Vai ficar para fila para tentar mais tarde.`,
          });

          failedCount++;
          remainingActionIds.add(id);
          continue; // Skip deletion, keep it in the queue for retry
        }

        syncedCount++;
        const deleteResponse = await deleteActionFromStore(id);
        if (deleteResponse.status === false) {
          console.log(deleteResponse.data);
          failedCount++;
          remainingActionIds.add(id);
          continue;
        }
        setPendingActions((prev) => prev.filter((a) => a.id !== id));
      }

      const remainingActions = actions.filter((action) => remainingActionIds.has(action.id));
      setPendingActions(remainingActions);

      if (syncedCount > 0 && remainingActions.length === 0) {
        message.success(`Sincronização completa! ${syncedCount} ações sincronizadas.`);

        // Send info capture to the server for debugging purposes
        await apiClient.post("/info-capture", {
          extraData: { location: "/components/layout-components/SyncPanel/index.jsx/syncActions/Synced", timestamp: new Date().toISOString() },
          subject: "Boats: Mobile SyncPanel",
          message: `Boats: SyncPanel: Synchronization complete! ${syncedCount} actions synchronized successfully. (${authUserName})`,
        });
      } else if (syncedCount > 0) {
        notification.warning({
          message: "Sincronização parcial",
          description: `Foram sincronizadas ${syncedCount} ações. ${remainingActions.length} continuam pendentes.`,
        });
        // Send info capture to the server for debugging purposes
        await apiClient.post("/info-capture", {
          extraData: { location: "/components/layout-components/SyncPanel/index.jsx/syncActions/Synced", timestamp: new Date().toISOString() },
          subject: "Boats: Mobile SyncPanel",
          message: `Boats: SyncPanel: Partial synchronization completed. ${syncedCount} actions synchronized successfully, ${remainingActions.length} actions remain pending. (${authUserName})`,
        });
      } else if (failedCount > 0) {
        notification.info({
          message: "Sincronização pendente",
          description: `${failedCount} ações continuam na fila e serão tentadas novamente.`,
        });

        // Send info capture to the server for debugging purposes
        await apiClient.post("/info-capture", {
          extraData: { location: "/components/layout-components/SyncPanel/index.jsx/syncActions/Synced", timestamp: new Date().toISOString() },
          subject: "Boats: Mobile SyncPanel",
          message: `Boats: SyncPanel: Synchronization failed for all actions. ${failedCount} actions remain in the queue for retry. (${authUserName})`,
        });
      }

      setSyncProgress(0);
      setSyncStatusMessage("Sincronização concluída.");
    } finally {
      setIsSyncing(false);
      setIsLoading(false);
      setSyncStatusMessage("A sincronizar ações pendentes...");
    }
  }

  syncActionsRef.current = syncActions;

  // Second branch of the same sync engine: push unsynced GPS location records in
  // batches to an idempotent endpoint (upsert by client id) and stamp synced_at only
  // on the ids the server confirms. Failures are retried on the next tick.
  async function syncLocationRecords() {
    if (!isOnline) return;
    // Guard against a stale online state queuing a spurious offline "/locations/batch" action.
    if (!isNetworkOnline()) return;
    if (locationSyncInFlightRef.current) return;
    locationSyncInFlightRef.current = true;

    try {
      const unsyncedResponse = await getUnsyncedLocationRecords();
      if (!unsyncedResponse || unsyncedResponse.status === false) return;

      const unsynced = unsyncedResponse.data || [];
      if (unsynced.length === 0) return;

      for (let index = 0; index < unsynced.length; index += LOCATION_SYNC_BATCH_SIZE) {
        const batch = unsynced.slice(index, index + LOCATION_SYNC_BATCH_SIZE);
        const response = await apiClient.post("/locations/batch", { locations: batch });

        // Failure: keep records unsynced and retry on the next tick.
        if (!response || response.status === false) break;

        const confirmedIds = response?.data?.confirmed_ids || [];
        if (confirmedIds.length > 0) {
          await markLocationRecordsSynced(confirmedIds);
        }
      }
    } catch (error) {
      console.error("syncLocationRecords error:", error);
    } finally {
      locationSyncInFlightRef.current = false;
    }
  }

  syncLocationRecordsRef.current = syncLocationRecords;

  const syncModal = <SyncBlockingModal open={isSyncing} message={syncStatusMessage} progress={syncProgress} pendingCount={pendingActions.length} />;

  if (!isOnline) return <>{syncModal}</>;
  if (pendingActions.length === 0)
    return (
      <>
        {syncModal}
        <div className="mr-3">
          <CheckCircleTwoTone twoToneColor={"#52c41a"} />
          <span style={{ marginLeft: 5 }}>Sincronizado</span>
        </div>
      </>
    );

  return (
    <>
      {syncModal}
      <div>
        <Badge showZero={false} overflowCount={999} count={pendingActions.length} style={{ backgroundColor: "#ff5f5f", marginRight: 10 }} />
        {isSyncing && <Progress style={{ width: 250 }} percent={syncProgress} status={isLoading ? "active" : "normal"} />}
        {isSyncing && <SyncOutlined spin={true} style={{ marginLeft: 5 }} />}
        {isLoading && !isSyncing && <LoadingOutlined style={{ marginLeft: 5 }} />}
        {!isSyncing && !isLoading && <HourglassOutlined style={{ marginLeft: 5 }} />}
      </div>
    </>
  );
};

export default Sync;
